import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Modal from './common/Modal';
import { IncomingPayment, incomingPaymentsApi, oftIncomingApi } from '../api/payments';
import { Client } from '../api/admin';
import { CountryListItem } from '../api/currencyCountries';
import { Provider } from '../api/providers';
import { getErrorMessage } from '../api/client';
import { getClientDisplayName } from '../utils/clientDisplay';

interface IncomingPaymentDetailModalProps {
  isOpen: boolean;
  paymentId: string | null;
  onClose: () => void;
  clients: Client[];
  providers: Provider[];
  countries: CountryListItem[];
  resolveCurrencyLabel: (rawCurrencyId: string | null | undefined) => string;
  source?: 'incoming' | 'oft';
}

type ParsedIncomingDetails = {
  beneficiaryName?: string;
  beneficiaryBankName?: string;
  beneficiaryAccountNumber?: string;
  beneficiarySwift?: string;
  recipientCountryId?: string;
  bankCountryId?: string;
  beneficiaryCity?: string;
  beneficiaryZipCode?: string;
  beneficiaryAddress?: string;
};

const parseIncomingDetails = (comment?: string | null): ParsedIncomingDetails => {
  if (!comment) return {};
  const cleaned = comment.replace(/\r/g, ' ').replace(/\n/g, ' ').trim();
  if (!cleaned) return {};
  const stripped = cleaned.replace(/^\[[^\]]+\]\s*/, '');
  const segments = stripped.split('|').map((segment) => segment.trim()).filter(Boolean);
  const result: ParsedIncomingDetails = {};
  for (const segment of segments) {
    const parts = segment.split(';').map((part) => part.trim()).filter(Boolean);
    for (const part of parts) {
      const [rawKey, ...rest] = part.split('=');
      if (!rawKey || rest.length === 0) continue;
      const key = rawKey.trim();
      const value = rest.join('=').trim();
      if (!value) continue;
      if (key in result) continue;
      if (
        key === 'beneficiaryName' ||
        key === 'beneficiaryBankName' ||
        key === 'beneficiaryAccountNumber' ||
        key === 'beneficiarySwift' ||
        key === 'recipientCountryId' ||
        key === 'bankCountryId' ||
        key === 'beneficiaryCity' ||
        key === 'beneficiaryZipCode' ||
        key === 'beneficiaryAddress'
      ) {
        result[key as keyof ParsedIncomingDetails] = value;
      }
    }
  }
  return result;
};

function formatMoney(amount: number, currency: string): string {
  try {
    return amount.toLocaleString('en-US', { style: 'currency', currency, minimumFractionDigits: 2 });
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
}

function IncomingPaymentDetailModal({
  isOpen,
  paymentId,
  onClose,
  clients,
  providers,
  countries,
  resolveCurrencyLabel,
  source = 'incoming',
}: IncomingPaymentDetailModalProps) {
  const [payment, setPayment] = useState<IncomingPayment | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen || !paymentId) {
      setPayment(null);
      setError('');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError('');
        if (source === 'oft') {
          const res = await oftIncomingApi.getById(paymentId);
          if (!cancelled) {
            const p = res.data;
            setPayment({
              ...p,
              clientId: p.clientId && p.clientId !== '00000000-0000-0000-0000-000000000000' ? p.clientId : null,
              clientAccountId:
                p.clientAccountId && p.clientAccountId !== '00000000-0000-0000-0000-000000000000'
                  ? p.clientAccountId
                  : null,
              paymentKind: 'DIGITAL_INCOMING',
            });
          }
        } else {
          try {
            const res = await incomingPaymentsApi.getById(paymentId);
            if (!cancelled) setPayment(res.data);
          } catch (primaryErr: unknown) {
            const notFound = axios.isAxiosError(primaryErr) && primaryErr.response?.status === 404;
            if (!notFound) throw primaryErr;
            const res = await oftIncomingApi.getById(paymentId);
            if (!cancelled) {
              const p = res.data;
              setPayment({
                ...p,
                clientId: p.clientId && p.clientId !== '00000000-0000-0000-0000-000000000000' ? p.clientId : null,
                clientAccountId:
                  p.clientAccountId && p.clientAccountId !== '00000000-0000-0000-0000-000000000000'
                    ? p.clientAccountId
                    : null,
                paymentKind: 'DIGITAL_INCOMING',
              });
            }
          }
        }
      } catch (err: unknown) {
        if (!cancelled) setError(getErrorMessage(err, 'Failed to load incoming payment'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, paymentId, source]);

  const providerName = useMemo(() => {
    if (!payment?.providerId) return '—';
    return providers.find((p) => p.id === payment.providerId)?.name || payment.providerId;
  }, [payment?.providerId, providers]);

  const clientLabel = useMemo(() => {
    if (!payment?.clientId) return 'Unmatched (suspense)';
    const client = clients.find((c) => c.id === payment.clientId);
    return getClientDisplayName(client) || payment.clientId;
  }, [payment?.clientId, clients]);

  const currencyCode = useMemo(() => {
    if (!payment) return '';
    return resolveCurrencyLabel(payment.currencyId) || payment.currencyId;
  }, [payment, resolveCurrencyLabel]);

  const parsedDetails = useMemo(
    () => parseIncomingDetails(payment?.backofficeComment),
    [payment?.backofficeComment]
  );

  const countryNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of countries) {
      map.set(c.id, c.name);
    }
    return map;
  }, [countries]);

  const recipientCountry =
    parsedDetails.recipientCountryId ? countryNameById.get(parsedDetails.recipientCountryId) : null;
  const bankCountry =
    parsedDetails.bankCountryId ? countryNameById.get(parsedDetails.bankCountryId) : null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Incoming payment details" size="lg">
      {loading && <div className="text-sm text-gray-500 py-6">Loading…</div>}
      {!loading && error && <div className="text-sm text-red-600 py-2">{error}</div>}
      {!loading && !error && payment && (
        <div className="space-y-6 max-h-[75vh] overflow-y-auto pr-1">
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase">Payment ID</p>
            <p className="font-mono text-sm text-gray-900 break-all">{payment.id}</p>
          </div>

          <section className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase">Amount</p>
              <p className="font-semibold text-gray-900">{formatMoney(payment.amount, currencyCode)}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase">Fee</p>
              <p className="text-gray-900">{formatMoney(payment.feeAmount, currencyCode)}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase">Net credited</p>
              <p className="text-gray-900">{formatMoney(payment.netCredited, currencyCode)}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase">Status</p>
              <p className="text-gray-900">{payment.status === 'PENDING_CREDITING' ? 'Pending crediting' : payment.status}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase">Registered</p>
              <p className="text-gray-900">{new Date(payment.registeredAt).toLocaleString()}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase">Approved</p>
              <p className="text-gray-900">
                {payment.approvedAt ? new Date(payment.approvedAt).toLocaleString() : '—'}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase">Released</p>
              <p className="text-gray-900">
                {payment.releasedAt ? new Date(payment.releasedAt).toLocaleString() : '—'}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase">Under review</p>
              <p className="text-gray-900">
                {payment.underReviewAt ? new Date(payment.underReviewAt).toLocaleString() : '—'}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase">Currency</p>
              <p className="text-gray-900">{currencyCode}</p>
            </div>
          </section>

          <section className="border-t border-gray-100 pt-4 text-sm">
            <h4 className="text-sm font-semibold text-gray-900 mb-2">Provider &amp; reference</h4>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>
                <dt className="text-xs text-gray-500">Provider</dt>
                <dd className="text-gray-900">{providerName}</dd>
              </div>
              <div>
                <dt className="text-xs text-gray-500">Provider reference</dt>
                <dd className="text-gray-900">{payment.providerReference || '—'}</dd>
              </div>
            </dl>
          </section>

          <section className="border-t border-gray-100 pt-4 text-sm">
            <h4 className="text-sm font-semibold text-gray-900 mb-2">Client &amp; account</h4>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>
                <dt className="text-xs text-gray-500">Client</dt>
                <dd className="text-gray-900">{clientLabel}</dd>
              </div>
              <div>
                <dt className="text-xs text-gray-500">Client account</dt>
                <dd className="text-gray-900">{payment.clientAccountId || '—'}</dd>
              </div>
            </dl>
          </section>

          <section className="border-t border-gray-100 pt-4 text-sm">
            <h4 className="text-sm font-semibold text-gray-900 mb-2">Beneficiary</h4>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div className="sm:col-span-2">
                <dt className="text-xs text-gray-500">Name</dt>
                <dd className="text-gray-900">{parsedDetails.beneficiaryName || '—'}</dd>
              </div>
              <div>
                <dt className="text-xs text-gray-500">Bank</dt>
                <dd className="text-gray-900">{parsedDetails.beneficiaryBankName || '—'}</dd>
              </div>
              <div>
                <dt className="text-xs text-gray-500">Account / IBAN</dt>
                <dd className="text-gray-900">{parsedDetails.beneficiaryAccountNumber || '—'}</dd>
              </div>
              <div>
                <dt className="text-xs text-gray-500">SWIFT</dt>
                <dd className="text-gray-900">{parsedDetails.beneficiarySwift || '—'}</dd>
              </div>
              <div>
                <dt className="text-xs text-gray-500">Recipient country</dt>
                <dd className="text-gray-900">{recipientCountry || parsedDetails.recipientCountryId || '—'}</dd>
              </div>
              <div>
                <dt className="text-xs text-gray-500">Bank country</dt>
                <dd className="text-gray-900">{bankCountry || parsedDetails.bankCountryId || '—'}</dd>
              </div>
              <div>
                <dt className="text-xs text-gray-500">City</dt>
                <dd className="text-gray-900">{parsedDetails.beneficiaryCity || '—'}</dd>
              </div>
              <div>
                <dt className="text-xs text-gray-500">ZIP</dt>
                <dd className="text-gray-900">{parsedDetails.beneficiaryZipCode || '—'}</dd>
              </div>
              <div className="sm:col-span-2">
                <dt className="text-xs text-gray-500">Address</dt>
                <dd className="text-gray-900">{parsedDetails.beneficiaryAddress || '—'}</dd>
              </div>
            </dl>
          </section>

          <section className="border-t border-gray-100 pt-4 text-sm">
            <h4 className="text-sm font-semibold text-gray-900 mb-2">Backoffice comment</h4>
            <div className="text-gray-700 whitespace-pre-wrap break-words">
              {payment.backofficeComment || '—'}
            </div>
          </section>
        </div>
      )}
    </Modal>
  );
}

export default IncomingPaymentDetailModal;
